import React, { forwardRef, useState, useEffect, useImperativeHandle, useCallback } from 'react';
import { useNotification } from '../../hooks/useNotification';
import { useErrorInputs } from '../../hooks/useErrorInputs';
import { useDataAuth } from '../../hooks/useDataAuth';

import TableContainer from '@mui/material/TableContainer';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableHead from '@mui/material/TableHead';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import AddIcon from '@mui/icons-material/Add';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';


import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { deleteOffice, updateOffice } from '../../services/client.service';
import { getCountries, getStatesAndCities } from '../../services/sharedControls.service';
import { CreateBranch } from './dialogs/CreateBrach';
import { useTranslation } from 'react-i18next';

export const LocationClient = forwardRef(({ dto, handleDirectInputChange, isEdit, isView }, ref) => {
    const { t } = useTranslation();
    const initialState = {
        id_client_office: 0,
        id_company: dto.id_company || '',
        type_office: 'Oficina Principal',
        address: '',
        latitude: '',
        longitude: '',
        phone: '',
        cellphone: '',
        extension: '',
        email: '',
        postal_code: '',
        country: 'CO',
        state: '',
        city: '',
        edit: false
    }

    const [locationForm, setLocationForm] = useState(initialState);
    const [errorInputs, handleErrorInput] = useErrorInputs({});
    const [{ userSession }] = useDataAuth();

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [openDialodAddBranch, setOpenDialodAddBranch] = useState(false);
    const [idxEdit, setIdxEdit] = useState(null);
    const [sendNotification] = useNotification();

    const fetchData = useCallback(async () => {
        try {
            const response = await getCountries(userSession.token);
            if (response.data)
                setCountries(response.data);
        } catch (err) {
            console.log(err.message);
        }
    }, [userSession.token]);

    const GetStatesAndCities = useCallback(async (action, variables) => {
        try {
            const response = await getStatesAndCities(userSession.token, action, variables);
            if (response.data) {
                if (action === 'STATE')
                    setStates(response.data);
                else {
                    setCities(response.data);
                }
            }
        } catch (err) {
            console.log(err.message);
        }
    }, [userSession.token]);

    useEffect(() => {
        fetchData();
        GetStatesAndCities('STATE', { id_country: 1 });
    }, [fetchData, GetStatesAndCities]);


    const handleChangeCountryState = (e, action) => {
        const { name, value } = e.target;
        if (action === 'STATE') {
            setLocationForm((prev) => ({ ...prev, [name]: value }));
            if (value === 'CO')
                GetStatesAndCities('STATE', { id_country: 1 });
        }
        if (action === 'CITY') {
            setLocationForm((prev) => ({ ...prev, [name]: value }));
            GetStatesAndCities('CITY', { code: value });
        }
    }

    const validateField = (name, value) => {
        if (value === null || value === undefined || (typeof value === "string" && value.trim() === '')) {
            handleErrorInput(name, { isError: true, messageError: t("validation.requiredField") });
            return false;
        } else {
            handleErrorInput(name, { isError: false, messageError: '' });
            return true;
        }
    };


    useImperativeHandle(ref, () => ({
        getData: () => dto.location,
        errorInputs,
    }));

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setLocationForm((prev) => ({ ...prev, [name]: value }));
    };


    const handleAddBranch = () => {
        if (!ValidForm()) {
            sendNotification({
                msg: t("validation.requiredFieldsMissing"),
                variant: "error"
            })
            return false;
        }
        if (locationForm.edit) {
            dto.location.splice(idxEdit, 1, locationForm);
            handleDirectInputChange('location', dto.location);
            updateOffice(userSession.token, locationForm);
            setIdxEdit(null);
        } else {
            dto.location.push(locationForm);
        }
        setLocationForm(initialState);
        setOpenDialodAddBranch(false);
        sendNotification({
            msg: t("messages.saveSuccess"),
            variant: "success"
        })
    };

    const ValidForm = () => {
        let hasError = true;
        const excludedFields = ["longitude", "latitude", "id_company", "postal_code", "phone", "extension", "configuration"];

        Object.keys(locationForm)
            .filter(field => !excludedFields.includes(field))
            .forEach((field) => {
                const isValid = validateField(field, locationForm[field]);
                if (!isValid) hasError = isValid;
            });
        return hasError;
    };

    const handleEditBranch = (index) => {
        const branchToEdit = dto.location[index];
        GetStatesAndCities('CITY', { code: branchToEdit.state });
        setLocationForm(branchToEdit);
        handleOpenDialog();
        branchToEdit.edit = true;
        setIdxEdit(index);
    };

    const handleDeleteBranch = (index) => {
        if (isEdit) {
            const id_client_office = dto.location[index]?.id_client_office
            deleteOffice(userSession.token, { id_client_office });
        }
        dto.location.splice(index, 1);
        handleDirectInputChange('location', dto.location);
    };
    const handleOpenDialog = () => setOpenDialodAddBranch(true);
    const handleClose = () => {
        setOpenDialodAddBranch(false);
        setLocationForm(initialState);
    };

    return (
        <>
            <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography variant="h6">
                        {t("client.officeBranchLocation")}
                    </Typography>
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title={'Agregar Sucursal'}>
                            <IconButton
                                aria-label="close"
                                onClick={handleOpenDialog}
                                sx={{ color: '#6728AF' }}
                                disabled={isView}
                            >
                                <AddIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Grid>
                <Grid size={{ xs: 12}}>
                    {dto.location.length > 0 && (
                        <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
                            <Table>
                                <TableHead sx={{ backgroundColor: '#e0e0e0' }}>
                                    <TableRow>
                                        <TableCell>Acciones</TableCell>
                                        <TableCell>Tipo</TableCell>
                                        <TableCell>Dirección</TableCell>
                                        <TableCell>Teléfono</TableCell>
                                        <TableCell>Celular</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>País</TableCell>
                                        <TableCell>Departamento</TableCell>
                                        <TableCell>Ciudad</TableCell>
                                        <TableCell>C_Postal</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {dto.location.map((branch, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <Tooltip title="Editar">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleEditBranch(index)}
                                                        disabled={isView}
                                                    >
                                                        <Edit />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Eliminar">
                                                    <IconButton
                                                        color="error"
                                                        onClick={() => handleDeleteBranch(index)}
                                                        disabled={isView}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{branch.type_office}</TableCell>
                                            <TableCell>{branch.address}</TableCell>
                                            <TableCell>{branch.phone}</TableCell>
                                            <TableCell>{branch.cellphone}</TableCell>
                                            <TableCell>{branch.email}</TableCell>
                                            <TableCell>{branch.country}</TableCell>
                                            <TableCell>{branch.state}</TableCell>
                                            <TableCell>{branch.city}</TableCell>
                                            <TableCell>{branch.postal_code}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Grid>
                <CreateBranch
                    openDialodAddBranch={openDialodAddBranch}
                    setOpenDialodAddBranch={setOpenDialodAddBranch}
                    initialState={initialState}
                    locationForm={locationForm}
                    handleClose={handleClose}
                    handleAddBranch={handleAddBranch}
                    handleInputChange={handleInputChange}
                    handleDirectInputChange={handleDirectInputChange}
                    handleChangeCountryState={handleChangeCountryState}
                    errorInputs={errorInputs}
                    countries={countries}
                    states={states}
                    cities={cities}
                />
            </Grid>
        </>
    )
});